<script lang="ts" setup>
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import SocialQrDialog from '@/components/createdUi/SocialQrDialog.vue'
import type { ISocialLink } from '@/utils/socialLinks'
import { SOCIAL_LINKS } from '@/utils/socialLinks'
import { version } from '@/version'
import LogoSmall from '@images/logo/LogoSmall.svg?url'
import { layoutConfig } from '@layouts'
import { VerticalNavGroup, VerticalNavLink, VerticalNavSectionTitle } from '@layouts/components'
import { useLayoutConfigStore } from '@layouts/stores/config'
import { injectionKeyIsVerticalNavHovered } from '@layouts/symbols'
import type { Component } from 'vue'
import { VNodeRenderer } from './VNodeRenderer'

import { PORTAL_FEATURES } from '@/environment/environment'
import type { ExtendedNavLink, NavGroup, NavLink, NavSectionTitle, VerticalNavItems } from '@layouts/types'

interface Props {
  tag?: string | Component
  navItems: VerticalNavItems
  isOverlayNavActive: boolean
  toggleIsOverlayNavActive: (value: boolean) => void
}

const props = withDefaults(defineProps<Props>(), {
  tag: 'aside',
})

const refNav = ref()

const isHovered = useElementHover(refNav)

provide(injectionKeyIsVerticalNavHovered, isHovered)

const configStore = useLayoutConfigStore()

const resolveNavItemComponent = (
  item: NavLink | NavSectionTitle | NavGroup | ExtendedNavLink,
): Component => {
  if ('component' in item && item.component) return item.component
  if ('heading' in item) return VerticalNavSectionTitle
  if ('children' in item) return VerticalNavGroup
  return VerticalNavLink
}

/*
  ℹ️ Close overlay side when route is changed
  Close overlay vertical nav when link is clicked
*/
const route = useRoute()

watch(() => route.name, () => {
  props.toggleIsOverlayNavActive(false)
})

const isVerticalNavScrolled = ref(false)
const updateIsVerticalNavScrolled = (val: boolean) => isVerticalNavScrolled.value = val

const handleNavScroll = (evt: Event) => {
  isVerticalNavScrolled.value = (evt.target as HTMLElement).scrollTop > 0
}

const hideTitleAndIcon = configStore.isVerticalNavMini(isHovered)

const isSocialDialogOpen = ref(false)
const activeSocial = ref<ISocialLink | null>(null)

function openSocial(social: ISocialLink) {
  activeSocial.value = social
  isSocialDialogOpen.value = true
}

// Список разрешённых фич: K8s (portal-env.js) или плейсхолдеры после sed в Docker
const enabledFeatures = new Set(
  PORTAL_FEATURES.split(',')
    .map((f: string) => f.trim())
    .filter(Boolean),
)

// Фильтруем navItems
const filteredNavItems = computed(() =>
  props.navItems.filter(item => {
    // Временно скрытые пункты (старые чаты) — код сохранён
    if ('disabledNav' in item && item.disabledNav) {
      return false
    }
    // Если нет поля feature, показываем всегда (например, Divider)
    if (!('feature' in item)) {
      return true
    }
    // Показываем только если feature разрешена
    return enabledFeatures.has(item.feature as string)
  }),
)
</script>

<template>
  <Component
    :is="props.tag"
    ref="refNav"
    data-allow-mismatch
    class="layout-vertical-nav"
    :class="[
      {
        'overlay-nav': configStore.isLessThanOverlayNavBreakpoint,
        'hovered': isHovered,
        'visible': isOverlayNavActive,
        'scrolled': isVerticalNavScrolled,
      },
    ]"
  >
    <!-- 👉 Header -->
    <div class="nav-header">
      <slot name="nav-header">
        <RouterLink
          to="/"
          class="app-logo app-title-wrapper"
        >
          <img
            v-if="configStore.isVerticalNavCollapsed && !isHovered"
            :src="LogoSmall"
            alt="Logo Small"
            style="width: 34px; height: 34px;"
          >
          <VNodeRenderer v-else :nodes="layoutConfig.app.logo" />

          <Transition name="vertical-nav-app-title">
            <h1
              v-show="!hideTitleAndIcon"
              class="app-logo-title leading-normal"
            >
              {{ layoutConfig.app.title }}
            </h1>
          </Transition>
        </RouterLink>
        <!-- 👉 Vertical nav actions -->
        <!-- Show toggle collapsible in >md and close button in <md -->
        <div class="header-action">
          <!-- стрелка влево — только при раскрытом меню -->
          <Component
            :is="layoutConfig.app.iconRenderer || 'div'"
            v-show="!configStore.isVerticalNavCollapsed"
            class="d-none nav-pin"
            :class="!configStore.isVerticalNavCollapsed && 'd-lg-block'"
            v-bind="layoutConfig.icons.verticalNavPinned"
            @click="configStore.isVerticalNavCollapsed = true"
          />

          <!-- стрелка вправо (разворачивает) — только при свёрнутом -->
          <Component
            :is="layoutConfig.app.iconRenderer || 'div'"
            v-show="configStore.isVerticalNavCollapsed"
            class="d-none nav-unpin"
            :class="configStore.isVerticalNavCollapsed && 'd-lg-block'"
            v-bind="layoutConfig.icons.verticalNavUnPinned"
            @click="configStore.isVerticalNavCollapsed = false"
          />

          <Component
            :is="layoutConfig.app.iconRenderer || 'div'"
            class="d-lg-none"
            v-bind="layoutConfig.icons.close"
            @click="toggleIsOverlayNavActive(false)"
          />
        </div>
      </slot>
    </div>
    <slot name="before-nav-items">
      <!--      <div class="vertical-nav-items-shadow" /> -->
    </slot>
    <slot
      name="nav-items"
      :update-is-vertical-nav-scrolled="updateIsVerticalNavScrolled"
    >
      <ul
        class="nav-items"
        @scroll="handleNavScroll"
      >
        <template v-for="(item, index) in filteredNavItems" :key="index">
          <Component
            :is="item.component"
            v-if="'component' in item && item.component"
            v-bind="item.props || {}"
          />
          <Component
            :is="resolveNavItemComponent(item)"
            v-else
            :item="item"
          />
        </template>
      </ul>

      <div
        v-if="!hideTitleAndIcon"
        class="app-version-static"
      >
        <CustomDivider />
        <div class="d-flex flex-column align-center gap-2">
          <span class="app-socials-title">Подписывайтесь на нас:</span>
          <div class="d-flex align-center gap-4">
            <VTooltip
              v-for="social in SOCIAL_LINKS"
              :key="social.id"
              :text="social.title"
              location="top"
            >
              <template #activator="{ props: tooltipProps }">
                <button
                  v-bind="tooltipProps"
                  type="button"
                  class="app-socials-btn"
                  :aria-label="social.title"
                  @click="openSocial(social)"
                  v-html="social.icon"
                />
              </template>
            </VTooltip>
          </div>
        </div>
        <div class="app-version d-flex flex-column justify-center align-center">
          <span>ООО "Дао АйТи" © 2025-2026</span>
          <span>v {{ version }}</span>
        </div>
      </div>
    </slot>

    <slot name="after-nav-items" />

    <SocialQrDialog
      v-model="isSocialDialogOpen"
      :social="activeSocial"
    />
  </Component>
</template>

<style lang="scss" scoped>
.app-logo {
  display: flex;
  align-items: center;
  column-gap: 0.75rem;

  .app-logo-title {
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
    text-transform: uppercase;
  }
}
</style>

<style lang="scss">
@use "@configured-variables" as variables;
@use "@layouts/styles/mixins";

/* text-primary / text-secondary макета — это on-surface с высокой и средней насыщенностью, как у пунктов меню, а не одноимённые токены theme.ts */
.app-version {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-feature-settings: "liga" off, "clig" off;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
}

.app-socials-title {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  opacity: 0.92;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
}

/* Через родителя: иначе цвет перебивает глобальный сброс `button { color: inherit }` */
.app-version-static .app-socials-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  block-size: 2rem;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  opacity: 0.92;
  cursor: pointer;
}

// 👉 Vertical Nav
.layout-vertical-nav {
  position: fixed;
  z-index: variables.$layout-vertical-nav-z-index;
  display: flex;
  flex-direction: column;
  block-size: 100%;
  inline-size: variables.$layout-vertical-nav-width;
  inset-block-start: 0;
  inset-inline-start: 0;
  transition: inline-size 0.25s ease-in-out, box-shadow 0.25s ease-in-out;
  will-change: transform, inline-size;
  border-right: 1px solid rgba(var(--v-theme-on-surface), var(--v-custom-opacity));

  .nav-header {
    display: flex;
    margin: 0 !important;
    align-items: center;
    background-color: rgb(var(--v-theme-paper));

    block-size: variables.$layout-vertical-nav-navbar-height;
    min-block-size: variables.$layout-vertical-nav-navbar-height;
    box-sizing: border-box;
    padding-inline: variables.$vertical-nav-horizontal-padding;
    padding-block: 0;

    .header-action {
      cursor: pointer;

      @at-root {
        #{variables.$selector-vertical-nav-mini} .nav-header .header-action {
          &.nav-pin,
          &.nav-unpin {
            display: none !important;
          }
        }
      }
    }
  }

  .app-title-wrapper {
    margin-inline-end: auto;
  }

  .nav-items {
    block-size: 100%;
    background-color: rgb(var(--v-theme-paper));
    overflow-x: hidden;
    overflow-y: auto;
    min-block-size: 0;
    scrollbar-width: thin;
    scrollbar-color: rgba(var(--v-theme-on-surface), 0.3) transparent;
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb {
      background: rgba(var(--v-theme-on-surface), 0.3);
      border-radius: 6px;
    }
  }

  .nav-item-title {
    margin-inline-end: auto;
    white-space: nowrap;
    overflow: visible;
    text-overflow: unset;
  }

  // 👉 Collapsed
  .layout-vertical-nav-collapsed & {
    &:not(.hovered) {
      inline-size: variables.$layout-vertical-nav-collapsed-width;
    }
  }
}

// Small screen vertical nav transition
@media (max-width: 1279px) {
  .layout-vertical-nav {
    &:not(.visible) {
      transform: translateX(-#{variables.$layout-vertical-nav-width});

      @include mixins.rtl {
        transform: translateX(variables.$layout-vertical-nav-width);
      }
    }

    transition: transform 0.25s ease-in-out;
  }
}

.layout-vertical-nav-collapsed .layout-vertical-nav:not(.hovered) {
  .custom-divider {
    .text,
    .line.left {
      display: none;
    }

    .line.right {
      width: calc(100% - 2.5rem);
      flex: none;
      margin: 0 auto;
    }
  }
}

.layout-vertical-nav-collapsed .layout-vertical-nav.hovered {
  box-shadow: 4px 0 10px rgba(46, 38, 61, 0.1) !important;
}

.app-version-static {
  padding: 1rem;
  background-color: rgb(var(--v-theme-paper));
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.layout-vertical-nav:not(.hovered) .nav-unpin {
  display: none !important;
}
</style>
